import { useEffect } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import { FileTree } from '@dbt-labs/sourdough';

import { Spinner } from '@/components/Spinner';
import {
  FileTreeProvider,
  ROOT_NODE_ID,
  useFileTree,
} from '@/context/FileTreeProvider';
import { useLinkToExplorer } from '@/hooks/links';
import { ProjectScope, useUrlProjectParams, useValidatedParams } from '@/hooks/params';

type FileTreeFilterDisplayProps = {
  dbtUniqueId?: string;
  projectScope: ProjectScope;
  navigate: NavigateFunction;
};

export const FileTreeFilterDisplay = ({
  dbtUniqueId,
  projectScope,
  navigate,
}: FileTreeFilterDisplayProps) => {
  const getDetailsUrl = useLinkToExplorer('resourceDetails');

  const {
    selectUniqueId,
    clearActiveFile,
    openDirectories,
    setOpenDirectories,
    visibleItems,
    activeFilePath,
    fileToUniqueIdMap,
  } = useFileTree();

  useEffect(() => {
    if (dbtUniqueId) {
      selectUniqueId(dbtUniqueId);
    } else {
      clearActiveFile();
    }
  }, [selectUniqueId, clearActiveFile, dbtUniqueId]);

  if (visibleItems?.length === 0) return <>No file tree</>;

  return (
    <>
      {!visibleItems && <Spinner />}
      <div
        className={`${
          visibleItems ? 'opacity-100' : 'opacity-0'
        } h-full w-full flex-1 flex-grow duration-300 motion-reduce:duration-0`}
      >
        {visibleItems && (
          <FileTree
            items={visibleItems}
            openDirectories={openDirectories}
            setOpenDirectories={setOpenDirectories}
            selectedFile={activeFilePath}
            rootNodeName={ROOT_NODE_ID}
            isReadOnly={true}
            onFileSelect={(path: string) => {
              const targetUniqueId = fileToUniqueIdMap?.[path]?.[0];
              if (!targetUniqueId) return;
              const targetUrl = getDetailsUrl({
                ...projectScope,
                dbtUniqueId: targetUniqueId,
              });

              navigate(targetUrl);
            }}
          />
        )}
      </div>
    </>
  );
};

export const FileTreeFilter = () => {
  const { dbtUniqueId } = useValidatedParams();

  const projectScope = useUrlProjectParams();
  const navigate = useNavigate();

  return (
    <FileTreeProvider>
      <FileTreeFilterDisplay
        dbtUniqueId={dbtUniqueId}
        projectScope={projectScope}
        navigate={navigate}
      />
    </FileTreeProvider>
  );
};
